import styled from 'styled-components';
import Card from '../../../components/Card';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  Heading1Text,
  Heading4Text,
  ParagraphLargeText,
  ParagraphMediumText,
  TitleLarge,
} from '../../../components/Texts';
import { useGetGiftsQuery } from '../../../redux/api/authorized/gifts';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../redux/slices/auth';
import GiftedCard from '../../../components/GiftedCard';
import Button from '../../../components/Button/index';
import { useNavigate } from 'react-router-dom';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import GiftsListSkeleton from './GiftsListSkeleton';
import { useTheme } from 'styled-components';

const GiftsContainer = styled.div`
  padding-top: 32px;
`;

const GiftsSection = styled.div`
  margin-bottom: 40px;
`;

const GiftsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const EmptyStateCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 327px;
`;

const EmptyStateLine = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  width: ${({ width }) => width};
  border-radius: 16px;
  height: 22px;
`;

const EmptyStateCard = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  width: 100%;
  height: 150px;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  position: relative;
`;

const EmptyStateTextContainer = styled.div`
  position: absolute;
  display: flex;
  gap: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
const EmptyState = ({ setSelectedTab }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const navigate = useNavigate();

  return (
    <EmptyStateContainer>
      {isMobile ? (
        <EmptyStateCardContainer>
          <EmptyStateCard />
          <EmptyStateLine width='100%' />
          <EmptyStateLine width='50%' />
          <EmptyStateLine width='70%' />
        </EmptyStateCardContainer>
      ) : (
        <>
          <EmptyStateCardContainer>
            <EmptyStateCard />
            <EmptyStateLine width='100%' />
            <EmptyStateLine width='50%' />
            <EmptyStateLine width='70%' />
          </EmptyStateCardContainer>
          <EmptyStateCardContainer>
            <EmptyStateCard />
            <EmptyStateLine width='100%' />
            <EmptyStateLine width='50%' />
            <EmptyStateLine width='70%' />
          </EmptyStateCardContainer>
          <EmptyStateCardContainer>
            <EmptyStateCard />
            <EmptyStateLine width='100%' />
            <EmptyStateLine width='50%' />
            <EmptyStateLine width='70%' />
          </EmptyStateCardContainer>
        </>
      )}
      <EmptyStateTextContainer>
        <Heading4Text>No gifts yet</Heading4Text>
        <Button
          onClick={() => {
            navigate('/');
          }}
        >
          Buy a gift
        </Button>
      </EmptyStateTextContainer>
    </EmptyStateContainer>
  );
};

const Gifts = ({ setSelectedTab }) => {
  const token = useSelector(selectPrivyToken);

  const { data: gifts, isLoading } = useGetGiftsQuery({
    skip: !token,
  });

  const theme = useTheme();
  const { sentGifts, receivedGifts } = gifts || {
    sentGifts: [],
    receivedGifts: [],
  };

  // Sort gifts to show HTLC_CREATED status first
  const sortGiftsByStatus = (gifts) => {
    return [...gifts].sort((a, b) => {
      // HTLC_CREATED gifts should come first
      if (a.status === 'HTLC_CREATED' && b.status !== 'HTLC_CREATED') {
        return -1;
      }
      if (b.status === 'HTLC_CREATED' && a.status !== 'HTLC_CREATED') {
        return 1;
      }
      // For all other cases, maintain original order
      return 0;
    });
  };

  const sortedSentGifts = sortGiftsByStatus(sentGifts);
  const sortedReceivedGifts = sortGiftsByStatus(receivedGifts);

  return (
    <GiftsContainer>
      <GiftsSection>
        <ParagraphLargeText style={{ color: theme.text.title }}>
          Sent
        </ParagraphLargeText>
        {isLoading ? (
          <GiftsListSkeleton />
        ) : (
          <>
            {sortedSentGifts.length > 0 ? (
              <GiftsGrid>
                {sortedSentGifts.map((gift) => (
                  <GiftedCard key={gift.id} card={gift} />
                ))}
              </GiftsGrid>
            ) : (
              <EmptyState setSelectedTab={setSelectedTab} />
            )}
          </>
        )}
      </GiftsSection>
      <GiftsSection>
        <ParagraphLargeText style={{ color: theme.text.title }}>
          Received
        </ParagraphLargeText>
        {isLoading ? (
          <GiftsListSkeleton />
        ) : (
          <>
            {sortedReceivedGifts.length > 0 ? (
              <GiftsGrid>
                {sortedReceivedGifts.map((gift) => (
                  <GiftedCard received key={gift.id} card={gift} />
                ))}
              </GiftsGrid>
            ) : (
              <EmptyState setSelectedTab={setSelectedTab} />
            )}
          </>
        )}
      </GiftsSection>
    </GiftsContainer>
  );
};

export default Gifts;
